import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { corpusService, CorpusWordWithSectors, UpdateWordData } from '../../services/corpusService';
import { CorpusSector } from '../../types';
import { Button } from '../ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';

interface EditWordModalProps {
  isOpen: boolean;
  onClose: () => void;
  word: CorpusWordWithSectors | null;
  sectors: CorpusSector[];
}

export default function EditWordModal({
  isOpen,
  onClose,
  word,
  sectors: _sectors,
}: EditWordModalProps) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<UpdateWordData>({
    word: '',
    normalizedWord: '',
    mcerlLevel: 'B2',
  });

  useEffect(() => {
    if (word) {
      setFormData({
        word: word.word,
        normalizedWord: word.normalizedWord,
        mcerlLevel: word.mcerlLevel,
        syllableCount: word.syllableCount || undefined,
        complexityScore: word.complexityScore ? Number(word.complexityScore) : undefined,
        frequencyScore: word.frequencyScore ? Number(word.frequencyScore) : undefined,
        partOfSpeech: word.partOfSpeech || undefined,
        lemma: word.lemma || undefined,
        stem: word.stem || undefined,
        wordType: word.wordType || 'GENERAL',
        sectorIds: word.sectors?.map(s => s.sectorId) || [],
      });
    }
  }, [word]);

  const updateMutation = useMutation({
    mutationFn: (data: { id: number; updateData: UpdateWordData }) =>
      corpusService.updateWord(data.id, data.updateData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['corpus-words'] });
      queryClient.invalidateQueries({ queryKey: ['corpus-stats'] });
      toast.success('Palabra actualizada exitosamente');
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Error al actualizar palabra');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!word) return;

    if (!formData.word?.trim() || !formData.normalizedWord?.trim()) {
      toast.error('La palabra y su forma normalizada son obligatorias');
      return;
    }

    updateMutation.mutate({
      id: word.id,
      updateData: formData,
    });
  };

  if (!isOpen || !word) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-blue-600 text-white p-6 rounded-t-xl flex items-center justify-between">
          <h2 className="text-2xl font-bold">Editar Palabra</h2>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} className="text-xl" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Palabra <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.word}
                onChange={(e) => setFormData({ ...formData, word: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Forma Normalizada <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.normalizedWord}
                onChange={(e) => setFormData({ ...formData, normalizedWord: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Nivel MCERL <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.mcerlLevel}
                onChange={(e) =>
                  setFormData({ ...formData, mcerlLevel: e.target.value as any })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="A1">A1</option>
                <option value="A2">A2</option>
                <option value="B1">B1</option>
                <option value="B2">B2</option>
                <option value="C1">C1</option>
                <option value="C2">C2</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Sílabas</label>
              <input
                type="number"
                value={formData.syllableCount || ''}
                onChange={(e) =>
                  setFormData({ ...formData, syllableCount: e.target.value ? parseInt(e.target.value) : undefined })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Tipo de Palabra
              </label>
              <select
                value={formData.wordType || 'GENERAL'}
                onChange={(e) =>
                  setFormData({ ...formData, wordType: e.target.value as any })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="GENERAL">General</option>
                <option value="TECHNICAL">Técnica</option>
                <option value="SPECIALIZED">Especializada</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Lema</label>
              <input
                type="text"
                value={formData.lemma || ''}
                onChange={(e) => setFormData({ ...formData, lemma: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Categoría Gramatical
              </label>
              <input
                type="text"
                value={formData.partOfSpeech || ''}
                onChange={(e) => setFormData({ ...formData, partOfSpeech: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="sustantivo, verbo, adjetivo..."
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Puntuación de Complejidad
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.complexityScore || ''}
                onChange={(e) =>
                  setFormData({ ...formData, complexityScore: e.target.value ? parseFloat(e.target.value) : undefined })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Puntuación de Frecuencia
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.frequencyScore || ''}
                onChange={(e) =>
                  setFormData({ ...formData, frequencyScore: e.target.value ? parseFloat(e.target.value) : undefined })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" onClick={onClose} variant="outline" className="px-6">
              Cancelar
            </Button>
            <Button
              type="submit"
              className="px-6 bg-blue-600 hover:bg-blue-700"
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Actualizando...' : 'Actualizar Palabra'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
